import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";

const API = process.env.REACT_APP_BACKEND_URL;

const TXT = {
  en: {
    titleReset: "Choose a new password", titleForgot: "Reset your password",
    introForgot: "Enter your email address and we will send you a reset link.",
    email: "Email address", newPw: "New password (min. 8 characters)",
    sendLink: "Send reset link", save: "Save new password",
    sent: "If an account exists for this email address, a reset link has been sent. Check your inbox.",
    done: "Your password has been updated. You can now sign in with your new password.",
    toLogin: "Go to sign in", invalid: "Invalid or expired reset link. Request a new one below.",
    genericError: "Something went wrong. Please try again.",
  },
  nl: {
    titleReset: "Kies een nieuw wachtwoord", titleForgot: "Wachtwoord opnieuw instellen",
    introForgot: "Vul je e-mailadres in en we sturen je een herstellink.",
    email: "E-mailadres", newPw: "Nieuw wachtwoord (min. 8 tekens)",
    sendLink: "Verstuur herstellink", save: "Nieuw wachtwoord opslaan",
    sent: "Als er een account bestaat voor dit e-mailadres, is er een herstellink verstuurd. Controleer je inbox.",
    done: "Je wachtwoord is bijgewerkt. Je kunt nu inloggen met je nieuwe wachtwoord.",
    toLogin: "Naar inloggen", invalid: "Ongeldige of verlopen herstellink. Vraag hieronder een nieuwe aan.",
    genericError: "Er ging iets mis. Probeer het opnieuw.",
  },
};

export default function ResetPassword() {
  const { lang } = useT();
  const t = TXT[lang] || TXT.en;
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // sent | done | error-text
  const [expired, setExpired] = useState(false);

  useSEO({
    title: lang === "nl" ? "Wachtwoord opnieuw instellen — StoicOs.ai" : "Reset Password — StoicOs.ai",
    description: lang === "nl" ? "Stel je StoicOs.ai wachtwoord opnieuw in." : "Reset your StoicOs.ai password.",
    canonical: "https://stoicos.ai/reset-password",
  });

  const submitForgot = async (e) => {
    e.preventDefault(); setBusy(true); setStatus(null);
    try { await axios.post(`${API}/api/auth/forgot-password`, { email }); setStatus("sent"); }
    catch (_) { setStatus("sent"); }
    finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault(); setBusy(true); setStatus(null);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: password });
      setStatus("done");
    } catch (err) {
      if (err?.response?.status === 400) setExpired(true);
      else setStatus(err?.response?.data?.detail || t.genericError);
    } finally { setBusy(false); }
  };

  const showReset = token && !expired && status !== "done";
  const inputCls = "w-full bg-black/40 border border-white/15 focus:border-gold/50 outline-none px-4 py-3.5 text-bone text-sm placeholder:text-bone/30 min-h-[48px]";
  const btnCls = "w-full min-h-[48px] bg-gold text-ink text-[11px] tracking-[0.24em] uppercase font-semibold hover:bg-gold/90 transition-colors disabled:opacity-50";

  return (
    <main data-testid="reset-password-page" className="min-h-screen bg-ink pt-32 pb-32 px-5">
      <div className="max-w-md mx-auto">
        <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">StoicOs.ai</p>
        <h1 className="font-serif text-3xl md:text-4xl text-bone mb-4" data-testid="reset-heading">
          {showReset ? t.titleReset : t.titleForgot}
        </h1>

        {expired && <p className="text-red-300/90 text-sm mb-6" data-testid="reset-invalid">{t.invalid}</p>}

        {status === "done" ? (
          <div data-testid="reset-done">
            <p className="text-bone/80 text-sm leading-relaxed mb-8">{t.done}</p>
            <Link to="/my-stoicos?mode=login" data-testid="reset-to-login"
              className="inline-block min-h-[48px] px-8 py-3.5 bg-gold text-ink text-[11px] tracking-[0.24em] uppercase font-semibold">
              {t.toLogin}
            </Link>
          </div>
        ) : status === "sent" ? (
          <p className="text-bone/80 text-sm leading-relaxed" data-testid="forgot-sent">{t.sent}</p>
        ) : showReset ? (
          <form onSubmit={submitReset} className="space-y-4">
            <input type="password" value={password} required minLength={8} maxLength={128}
              data-testid="reset-new-password" placeholder={t.newPw}
              onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            {status && typeof status === "string" && status !== "sent" && (
              <p className="text-red-300/90 text-sm" data-testid="reset-error">{status}</p>
            )}
            <button type="submit" disabled={busy || password.length < 8}
              data-testid="reset-submit" className={btnCls}>{busy ? "…" : t.save}</button>
          </form>
        ) : (
          <form onSubmit={submitForgot} className="space-y-4">
            <p className="text-bone/60 text-sm leading-relaxed mb-2">{t.introForgot}</p>
            <input type="email" value={email} required data-testid="forgot-email"
              placeholder={t.email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            <button type="submit" disabled={busy || !email} data-testid="forgot-submit"
              className={btnCls}>{busy ? "…" : t.sendLink}</button>
          </form>
        )}
      </div>
    </main>
  );
}
